// Session-lapse rules shared by the navigation middleware and the API client.
// ClientAPI (AVRillo-RD/AVRilloClientAPI#338) answers `GET /2fa/check-status` with
// `{ verified: false, reason: 'session-lapsed' }` and no channels once a token's OTP
// state is gone, and data routes answer 403 `{ requires_2fa_verification: true }`
// once enforcement is on. Both mean the client must sign in again.

export const SESSION_LAPSED_REASON = 'session-lapsed'

// Query value the login page reads to show SESSION_EXPIRED_MESSAGE.
export const SESSION_EXPIRED_REASON = 'session-expired'

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired, please sign in again.'

// Requests that must never trigger the lapsed-session redirect themselves.
const AUTHENTICATION_REQUEST_PATHS = ['/login', '/logout']
const TWO_FACTOR_REQUEST_PREFIX = '/2fa/'

// A check-status payload that cannot be answered with a code: the OTP state is gone
// (`reason`), or there is no channel to send a code to.
export function hasLapsedTwoFactorSession(status) {
  if (!status || status.verified) return false

  return status.reason === SESSION_LAPSED_REASON || !(Array.isArray(status.channels) && status.channels.length > 0)
}

// A data route refusing the token until a fresh OTP verification.
export function requiresTwoFactorVerification(error) {
  return error?.response?.status === 403 && error.response.data?.requires_2fa_verification === true
}

export function isAuthenticationRequest(url) {
  if (typeof url !== 'string') return false

  return AUTHENTICATION_REQUEST_PATHS.includes(url) || url.startsWith(TWO_FACTOR_REQUEST_PREFIX)
}

// The Authorization header the failed request was actually sent with. Axios leaves an
// AxiosHeaders instance on `error.config`; a plain object is read case-insensitively.
export function requestAuthorizationHeader(error) {
  const headers = error?.config?.headers
  if (!headers || typeof headers !== 'object') return null

  if (typeof headers.get === 'function') return headers.get('Authorization') ?? null

  const name = Object.keys(headers).find((key) => key.toLowerCase() === 'authorization')

  return name ? headers[name] ?? null : null
}

// A response belongs to the current session only if its request carried the session's token.
// After a sign-out and a fresh sign-in, a delayed answer from the earlier session does not.
export function isResponseForCurrentSession(error, currentAuthorizationHeader) {
  const sentWith = requestAuthorizationHeader(error)

  return typeof sentWith === 'string' && sentWith !== '' && sentWith === currentAuthorizationHeader
}

// Whether the session a request was made for is still the signed-in one. A sign-out or a
// newer sign-in during the request replaces it, and its delayed answers must be ignored.
export function isSessionCurrent(session, sessionHeader) {
  return session?.isAuthenticated === true && typeof sessionHeader === 'string' && session.authorizationHeader === sessionHeader
}

// Whether expireSession() has cleared the session a request was made for (a 2FA 403 or a
// lapsed check-status answer) and nothing has signed in since. A store cleared by an
// ordinary sign-out does not count (the client deliberately left), nor does one a newer
// sign-in has replaced, so their delayed 403s are ignored.
export function isSessionExpired(session, sessionHeader) {
  return session?.isAuthenticated === false && typeof sessionHeader === 'string' && session.expiredSessionHeader === sessionHeader
}

export const CheckStatusOutcome = Object.freeze({
  Ignore: 'ignore',
  Verified: 'verified',
  Lapsed: 'lapsed',
  Unverified: 'unverified',
})

// What a check-status answer means for the session (`sessionHeader`) that asked for it.
export function checkStatusOutcome(status, session, sessionHeader) {
  if (!isSessionCurrent(session, sessionHeader)) return CheckStatusOutcome.Ignore
  if (status?.verified) return CheckStatusOutcome.Verified
  if (hasLapsedTwoFactorSession(status)) return CheckStatusOutcome.Lapsed

  return CheckStatusOutcome.Unverified
}

// The API client's rule for a failed request: expire the session only when the client is
// still signed in with the token that request carried, and never for sign-in requests.
export function shouldExpireSession(error, session) {
  return (
    requiresTwoFactorVerification(error) &&
    !isAuthenticationRequest(error.config?.url) &&
    session.isAuthenticated === true &&
    isResponseForCurrentSession(error, session.authorizationHeader)
  )
}
